// Hook pour vérifier l'état de l'API
import { useState, useEffect } from 'react';

export interface ApiHealthStatus {
    isOnline: boolean;
    lastCheck: Date | null;
    latency: number | undefined;
}

export interface UseApiHealthOptions {
    apiUrl: string;
    checkInterval?: number; // en millisecondes
    enabled?: boolean;
    timeout?: number;
}

/**
 * Hook pour surveiller l'état de santé de l'API
 * Effectue un polling régulier de l'endpoint /health
 */
export function useApiHealth(options: UseApiHealthOptions): ApiHealthStatus {
    const {
        apiUrl,
        checkInterval = 30000, // 30 secondes par défaut
        enabled = true,
        timeout = 5000
    } = options;

    const [status, setStatus] = useState<ApiHealthStatus>({
        isOnline: true, // Optimiste par défaut
        lastCheck: null,
        latency: undefined
    });

    useEffect(() => {
        if (!enabled || !apiUrl) {
            return;
        }

        let isMounted = true;
        let intervalId: NodeJS.Timeout;

        const checkHealth = async () => {
            const startTime = Date.now();

            try {
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), timeout);

                const response = await fetch(`${apiUrl}/health`, {
                    method: 'GET',
                    signal: controller.signal
                });

                clearTimeout(timeoutId);

                const latency = Date.now() - startTime;

                if (!isMounted) return;

                if (response.ok) {
                    setStatus({
                        isOnline: true,
                        lastCheck: new Date(),
                        latency
                    });
                } else {
                    setStatus({
                        isOnline: false,
                        lastCheck: new Date(),
                        latency
                    });
                }
            } catch (error) {
                if (!isMounted) return;

                console.warn('Échec du check API health:', error);
                
                setStatus({
                    isOnline: false,
                    lastCheck: new Date(),
                    latency: undefined
                });
            }
        };

        // Check immédiat au montage
        checkHealth();

        // Puis polling régulier
        intervalId = setInterval(checkHealth, checkInterval);

        return () => {
            isMounted = false;
            clearInterval(intervalId);
        };
    }, [apiUrl, checkInterval, enabled, timeout]);

    return status;
}


